import { Box, Button, Typography } from "@mui/material";

import type { SelectedProduct } from "@/features/Generations/context/PilotStateContext";
import type { BlueprintMessage } from "@/types/creativesphere";

import { PurchasedProductCard } from "@/features/Profiles/components/PurchasedProductCard";
import { CachedOutlinedIcon, ImageOutlinedIcon } from "@/icons";

function RailLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: "block", letterSpacing: 0.6 }}
    >
      {children}
    </Typography>
  );
}

function RailBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 4,
          py: 3,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <RailLabel>{title}</RailLabel>
      </Box>
      <Box
        sx={{ px: 4, py: 4, display: "flex", flexDirection: "column", gap: 4 }}
      >
        {children}
      </Box>
    </Box>
  );
}

export type SetupRailProps = {
  product?: SelectedProduct;
  blueprint?: BlueprintMessage;
  primaryLabel: string;
  primaryDisabled: boolean;
  onPrimary: () => void;
  showRegenerate: boolean;
  regenerateDisabled: boolean;
  onRegenerate: () => void;
  /** Disables the product/blueprint pickers while a run is in flight. */
  pickersDisabled: boolean;
  onChooseProduct: () => void;
  onChooseBlueprint: () => void;
};

// Setup controls beside the canvas: the run/accept CTA and the chosen inputs.
export function SetupRail({
  product,
  blueprint,
  primaryLabel,
  primaryDisabled,
  onPrimary,
  showRegenerate,
  regenerateDisabled,
  onRegenerate,
  pickersDisabled,
  onChooseProduct,
  onChooseBlueprint,
}: SetupRailProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Button
        variant="contained"
        size="large"
        disabled={primaryDisabled}
        onClick={onPrimary}
      >
        {primaryLabel}
      </Button>
      {showRegenerate ? (
        <Button
          variant="contained"
          size="large"
          disabled={regenerateDisabled}
          onClick={onRegenerate}
        >
          Regenerate
        </Button>
      ) : null}

      <RailBlock title="What it will be made from">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <RailLabel>Pilot product</RailLabel>
          {product ? (
            <Box sx={{ maxWidth: 150 }}>
              <PurchasedProductCard
                imageSrc={product.image_url}
                imageAlt={product.title}
                name={product.title}
                subtitle=""
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No product selected yet.
            </Typography>
          )}
          <Button
            variant="outlined"
            fullWidth
            startIcon={<CachedOutlinedIcon />}
            disabled={pickersDisabled}
            onClick={onChooseProduct}
          >
            Choose product
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <RailLabel>Blueprint</RailLabel>
          {blueprint ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {blueprint.name}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 120,
                  borderRadius: 1,
                  overflow: "hidden",
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.disabled",
                }}
              >
                {blueprint.thumbnail_url ? (
                  <Box
                    component="img"
                    src={blueprint.thumbnail_url}
                    alt={blueprint.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <ImageOutlinedIcon fontSize="medium" />
                )}
              </Box>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CachedOutlinedIcon />}
                disabled={pickersDisabled}
                onClick={onChooseBlueprint}
              >
                Change blueprint
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                No blueprint selected yet.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                disabled={pickersDisabled}
                onClick={onChooseBlueprint}
              >
                Pick a blueprint
              </Button>
            </>
          )}
        </Box>
      </RailBlock>
    </Box>
  );
}
